import React, { useState } from 'react';
import { Persona } from '../../types/persona';
import { PersonaEditor } from './PersonaEditor';
import { deletePersona, generatePersonaImage } from '../../services/api';

interface PersonaDetailModalProps {
  persona: Persona;
  onClose: () => void;
  onDelete?: () => void;
}

type TabType = 'overview' | 'cognitive' | 'psychographics' | 'brand' | 'bio' | 'professional';

export const PersonaDetailModal: React.FC<PersonaDetailModalProps> = ({
  persona,
  onClose,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(persona.avatarUrl);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${persona.name}?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await deletePersona(persona.personaId);
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting persona:', error);
      alert('Failed to delete persona. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleGenerateAvatar = async () => {
    // Confirm if there's already an avatar
    if (currentAvatarUrl) {
      if (!window.confirm('Replace existing avatar with a new AI-generated one?')) {
        return;
      }
    }

    setIsGeneratingAvatar(true);
    setAvatarError(null);

    try {
      const newAvatarUrl = await generatePersonaImage(persona.personaId);
      setCurrentAvatarUrl(newAvatarUrl);
      // Update the persona object for immediate UI refresh
      persona.avatarUrl = newAvatarUrl;
    } catch (error: any) {
      console.error('Error generating avatar:', error);
      const errorMessage = error.message || 'Failed to generate avatar. Please try again.';
      setAvatarError(errorMessage);
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const tabs: { id: TabType; label: string; show: boolean }[] = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'cognitive', label: 'Cognitive', show: true },
    { id: 'psychographics', label: 'Psychographics', show: true },
    { id: 'brand', label: 'Brand', show: true },
    { id: 'bio', label: 'Bio', show: true },
    { id: 'professional', label: 'Professional', show: !!persona.professional_context },
  ];

  const visibleTabs = tabs.filter((tab) => tab.show);

  if (isEditing) {
    return (
      <PersonaEditor
        persona={persona}
        onSave={() => {
          setIsEditing(false);
        }}
        onCancel={() => {
          setIsEditing(false);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-5xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-500 to-secondary-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Avatar - show image if available, otherwise show initials */}
                {currentAvatarUrl ? (
                  <div className="w-16 h-16 rounded-full overflow-hidden ring-4 ring-white ring-opacity-50">
                    <img
                      src={currentAvatarUrl}
                      alt={persona.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-2xl font-bold text-primary-500">
                    {persona.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .substring(0, 2)
                      .toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-white">{persona.name}</h2>
                  <p className="text-primary-100">
                    {persona.demographics.age} • {persona.demographics.gender} •{' '}
                    {persona.demographics.location}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleGenerateAvatar}
                  disabled={isGeneratingAvatar}
                  className="flex items-center space-x-2 px-3 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Generate AI Avatar"
                >
                  {isGeneratingAvatar ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-sm">Generating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">Generate Avatar</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-md transition-colors"
                  title="Edit Persona"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="p-2 text-white hover:bg-red-500 hover:bg-opacity-20 rounded-md transition-colors disabled:opacity-50"
                  title="Delete Persona"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-md transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px px-6 space-x-8 overflow-x-auto">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[calc(100vh-20rem)] overflow-y-auto">
            {/* Avatar generation error message */}
            {avatarError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">Avatar Generation Failed</h3>
                    <p className="text-sm text-red-700 mt-1">{avatarError}</p>
                  </div>
                  <button
                    onClick={() => setAvatarError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Demographics</h3>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs text-gray-500">Occupation</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {persona.demographics.occupation}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Income</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {persona.demographics.income}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Family Status</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {persona.demographics.family_status}
                        </dd>
                      </div>
                      {persona.demographics.cultural_background && (
                        <div>
                          <dt className="text-xs text-gray-500">Cultural Background</dt>
                          <dd className="text-sm font-medium text-gray-900">
                            {persona.demographics.cultural_background}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Quick Stats</h3>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs text-gray-500">IQ</dt>
                        <dd className="text-sm font-medium text-gray-900">{persona.cognitive.iq}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Critical Thinking</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {persona.cognitive.critical_thinking}/100
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Brand Relationship</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {persona.brand_relationship.usage_status}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'cognitive' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* IQ */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IQ</label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-500 h-2 rounded-full"
                          style={{ width: `${(persona.cognitive.iq / 200) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {persona.cognitive.iq}
                      </span>
                    </div>
                  </div>

                  {/* Critical Thinking */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Critical Thinking
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-secondary-600 h-2 rounded-full"
                          style={{ width: `${persona.cognitive.critical_thinking}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {persona.cognitive.critical_thinking}
                      </span>
                    </div>
                  </div>

                  {/* Specific Knowledge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brand-Specific Knowledge
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${persona.cognitive.knowledge_specific}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {persona.cognitive.knowledge_specific}
                      </span>
                    </div>
                  </div>

                  {/* Category Knowledge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Knowledge
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-accent-600 h-2 rounded-full"
                          style={{ width: `${persona.cognitive.knowledge_category}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {persona.cognitive.knowledge_category}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Media Consumption
                    </label>
                    <p className="text-sm text-gray-900">{persona.cognitive.media_consumption}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Political Alignment
                    </label>
                    <p className="text-sm text-gray-900">{persona.cognitive.political_alignment}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Temporal Awareness
                    </label>
                    <p className="text-sm text-gray-900">{persona.cognitive.temporal_awareness}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'psychographics' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Values</label>
                  <div className="flex flex-wrap gap-2">
                    {persona.psychographics.values.map((value, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-indigo-800"
                      >
                        {value}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personality
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {persona.psychographics.personality}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Goals</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {persona.psychographics.goals}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'brand' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usage Status
                  </label>
                  <p className="text-sm text-gray-900">{persona.brand_relationship.usage_status}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Perception
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {persona.brand_relationship.perception}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pain Points
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {persona.brand_relationship.pain_points}
                  </p>
                </div>

                {persona.brand_relationship.key_drivers && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Key Drivers
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {persona.brand_relationship.key_drivers}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'bio' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Narrative Biography
                </label>
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {persona.narrative_bio}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'professional' && persona.professional_context && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {persona.professional_context.company_size && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <p className="text-sm text-gray-900">
                        {persona.professional_context.company_size}
                      </p>
                    </div>
                  )}

                  {persona.professional_context.industry && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <p className="text-sm text-gray-900">
                        {persona.professional_context.industry}
                      </p>
                    </div>
                  )}

                  {persona.professional_context.buying_role && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Buying Role
                      </label>
                      <p className="text-sm text-gray-900">
                        {persona.professional_context.buying_role}
                      </p>
                    </div>
                  )}

                  {persona.professional_context.budget_authority && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Budget Authority
                      </label>
                      <p className="text-sm text-gray-900">
                        {persona.professional_context.budget_authority}
                      </p>
                    </div>
                  )}
                </div>

                {persona.professional_context.evaluation_criteria &&
                  persona.professional_context.evaluation_criteria.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Evaluation Criteria
                      </label>
                      <ul className="list-disc list-inside space-y-1">
                        {persona.professional_context.evaluation_criteria.map((criterion, index) => (
                          <li key={index} className="text-sm text-gray-900">
                            {criterion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
